import * as context from 'api/base';
import { getChannelSettingByKey } from 'app/layouts/whitelabel/routes/generic/Helpers/GenericIndexHelper';
import { getNewBuyInsurancePathGeneric } from 'app/layouts/whitelabel/routes/generic/Helpers/HomePageHelper';
import store from 'app/store';

// Both logo columns are optional per channel, so their widths cannot be baked into the menu
// widths. 3 is the ceiling for the secondary logo: 4 would make the all-columns row total 13
// and wrap the tertiary logo onto its own line.
export const SECONDARY_LOGO_MD = 3;
export const TERTIARY_LOGO_MD = 2;

// A single link list wider than a third of the row reads as a stray block rather than a column.
const MAX_MENU_MD = 4;

// The footer row must always add up to 12. When it falls short MUI centres a narrow block and the
// footer reads as an island on a wide screen, which is exactly the reported bug: a channel with a
// secondary logo but no tertiary logo only reached 9 of 12. Deriving the menu widths from whichever
// logo columns are actually present keeps every channel on the same rhythm instead of giving each
// one a different amount of dead space.
export const getFooterMenuWidths = ({ hasSecondaryLogo, hasTertiaryLogo, menuCount }) => {
	if (menuCount < 1) return [];

	const remaining = 12 - (hasSecondaryLogo ? SECONDARY_LOGO_MD : 0) - (hasTertiaryLogo ? TERTIARY_LOGO_MD : 0);
	const base = Math.floor(remaining / menuCount);
	const leftover = remaining - base * menuCount;

	// Leftover columns go to the right-most menus because the address column carries the longest
	// unbroken strings in the footer and wraps first.
	return Array.from({ length: menuCount }, (_, index) =>
		Math.min(base + (index >= menuCount - leftover ? 1 : 0), MAX_MENU_MD)
	);
};

export const footerConfig = ({ companyDetailsResponse }) => {
	const infoSettings = context.getSettings();
	const channelProducts = store.getState()?.fuse.form.channelProducts;
	const referenceTokenDetails = store.getState()?.fuse.form.referenceTokenDetails;
	const { address, address2, address3, city, province, country, postalCode, contactNumber, contactEMail } =
		companyDetailsResponse?.value?.companyContactViewModel || {};
	const myHostPrefix = store.getState()?.fuse.form.mySite.hostPrefix;

	// Get address from channel settings
	const addressLine1 = getChannelSettingByKey('Address-Line1') || '';
	const addressLine2 = getChannelSettingByKey('Address-Line2') || '';
	const addressLine3 = getChannelSettingByKey('Address-Line3') || '';
	const addressEmail = getChannelSettingByKey('Email-Address') || '';
	const addressPhone = getChannelSettingByKey('Phone-Number') || '';
	const addressProvince = getChannelSettingByKey('Address-Province') || '';
	const addressCity = getChannelSettingByKey('Address-City') || '';
	const addressCountry = getChannelSettingByKey('Address-Country') || '';
	const addressPostalCode = getChannelSettingByKey('Address-PostalCode') || '';

	const channelAddress = [
		addressLine1,
		addressLine2,
		addressLine3,
		addressCity,
		addressProvince,
		addressCountry,
		addressPostalCode
	]
		.filter(part => part)
		.join(' ');

	return {
		menus: [
			{
				id: 1,
				title: infoSettings.textFooterHeader1,
				menuItems: [
					{
						title: infoSettings.footerFaqText,
						url: '/faq',
						isVisible: infoSettings?.footerMenuFaqVisible === 'true'
					},
					{
						title: infoSettings.footerTermsAndCondText,
						url: '/terms-and-conditions',
						isvisible: infoSettings?.footerMenuTermsAndConditionsVisible === 'true'
					},
					{
						title: infoSettings.footerPrivacyPolicyText,
						url: '/privacy-policy',
						isVisible: infoSettings?.footerMenuPrivacyPolicyVisible === 'true'
					},
					{
						title: 'Disclosure',
						url: '/disclosure',
						isVisible: infoSettings?.footerMenuDisclosureVisible === 'true'
					},
					{
						title: infoSettings.footerDetailsText,
						url: '/details',
						isVisible: infoSettings?.footerMenuDetailsVisible === 'true'
					}
				],
				xs: 12,
				sm: 6
			},
			{
				id: 2,
				title: infoSettings.textFooterHeader2,
				menuItems: [
					{
						title: infoSettings.footerContactText,
						url: '/contact',
						isVisible: infoSettings.footerMenuContactVisible === 'true'
					},
					{
						title: infoSettings.footerAboutText,
						url: '/about',
						isVisible: infoSettings.footerMenuAboutVisible === 'true'
					},
					{
						title: 'Buy Insurance',
						url: getNewBuyInsurancePathGeneric(channelProducts, referenceTokenDetails?.token, myHostPrefix),
						isVisible: infoSettings.footerMenuInsuranceVisible === 'true'
					},
					{
						title: 'Submit Claim',
						url: '/submit-claim',
						isVisible: infoSettings.footerMenuClaimVisible === 'true'
					}
				],
				xs: 12,
				sm: 6
			},
			{
				id: 3,
				title: infoSettings.textAddressHeader,
				menuItems: [
					channelAddress?.length > 0
						? {
								title: channelAddress,
								url: '#',
								isVisible: true
							}
						: {
								title: `${address || ''} ${address2 || ''} ${address3 || ''} ${city || ''} ${province || ''} ${
									country || ''
								} ${postalCode || ''}`,
								url: '#',
								isVisible: true
							},
					{
						title: addressPhone ? addressPhone : `${contactNumber || ''}`,
						url: contactNumber,
						isVisible: true
					},
					{
						title: addressEmail ? addressEmail : `${contactEMail || ''}`,
						url: `mailTo:${contactEMail}`,
						isVisible: true
					}
				],
				xs: 12,
				sm: 6
			}
		]
	};
};
